import type { ReactElement, ReactNode, Ref } from "react";
import { Children, cloneElement, createContext, forwardRef, useContext } from "react";
import type {
  TooltipInitialState,
  TooltipReferenceProps,
  TooltipStateReturn,
  TooltipProps,
} from "reakit";
import {
  useTooltipState,
  TooltipReference as BaseTooltipReference,
  Tooltip as BaseTooltip,
} from "reakit";
import { Box } from "../Box/Box";
import { Text } from "../Text/Text";
import type { StylesOptions } from "../hooks/useStyles";
import { useRequiredPropWarning } from "../hooks/useRequiredPropWarning";

const TooltipContext = createContext<Partial<TooltipStateReturn>>({});

/**
 * @deprecated
 */
export function Tooltip({ children, ...props }: TooltipInitialState & { children: ReactNode }) {
  const tooltip = useTooltipState(props);
  useRequiredPropWarning(Boolean(children), "Tooltip", "children");
  return <TooltipContext.Provider value={tooltip}>{children}</TooltipContext.Provider>;
}

type ReferenceProps = TooltipReferenceProps & { children: ReactElement };

export const TooltipReference = forwardRef(function (
  { children, ...props }: Partial<ReferenceProps>,
  ref: Ref<HTMLDivElement>
) {
  const tooltip = useContext(TooltipContext) as TooltipStateReturn;
  useRequiredPropWarning(Boolean(children), "TooltipReference", "children");
  return (
    <BaseTooltipReference {...tooltip} {...props} ref={ref}>
      {(referenceProps: TooltipProps) =>
        cloneElement(Children.only(children as ReactElement), referenceProps)
      }
    </BaseTooltipReference>
  );
});

type ContentProps = { children: ReactNode } & Partial<TooltipProps> & StylesOptions;

export const TooltipContent = forwardRef(function (
  { children, className, ...props }: ContentProps,
  ref: Ref<HTMLDivElement>
) {
  const tooltip = useContext(TooltipContext);
  useRequiredPropWarning(Boolean(children), "TooltipContent", "children");
  return (
    <BaseTooltip {...tooltip} {...props} ref={ref}>
      <Box p={"sp-12"} bg="gray-80" borderRadius={4} className={className}>
        <Text color="white" fontSize="fs-14" textAlign="center">
          {children}
        </Text>
      </Box>
    </BaseTooltip>
  );
});
